/**
 * DataSource: PAEBM (Plano de Ação de Emergência de Barragens de Mineração)
 * -------------------------------------------------------------------------
 * Hoje: lê banners + recomendações do Supabase (alimentados manualmente).
 * Futuro: receber PAEBM digital da Galvani — chatbot M11 consulta este
 *         documento (Telles: "deve haver um módulo referente ao PAEBM
 *         com a finalidade de disponibilizar o PAEBM digitalmente,
 *         alimentar o chatbot, permitir responder perguntas").
 */

import { supabase } from '@/lib/supabase';
import type {
  BannerPersistente, DamId, PontoEncontro, PontoEncontroRow, Recomendacao,
  ZasEdificacao, ZasEdificacaoRow,
} from '@/domain';
import {
  asBannerId, asDamId, asEngineerId, asRecomendacaoId,
  mapEdificacaoRow, mapPontoEncontroRow,
} from '@/domain';
import type { DataSourceMeta } from './types';

export interface PaebmDataSource extends DataSourceMeta {
  listBanners(damId?: DamId): Promise<BannerPersistente[]>;
  listRecomendacoes(damId?: DamId): Promise<Recomendacao[]>;
  /** Quando o PAEBM digital chegar: indexar e retornar resumo. POC: stub. */
  buscarNoPaebm(query: string): Promise<{ found: boolean; trecho?: string; pagina?: number }>;
  /** Edificações da ZAS (PAEBM Seção 10) para o mapa (M2). GUPM-195. */
  listEdificacoesZas(): Promise<ZasEdificacao[]>;
  /** Pontos de encontro (PAEBM Seção 8) para o mapa (M2). GUPM-195. */
  listPontosEncontro(): Promise<PontoEncontro[]>;
}

export const manualPaebm: PaebmDataSource = {
  mode: 'manual',
  pendingIntegration: {
    owner: 'Galvani + Walm',
    name: 'PAEBM digital',
    status: 'pendente',
    description:
      'Recebimento e homologação do novo PAEBM Galvani (PEND-02). Substituirá referência ' +
      'provisória ao PAEBM Mosaic Rev.2. Quando disponível, alimentará o chatbot do M11 ' +
      'e o módulo PAEBM dedicado proposto pelo Telles.',
  },

  async listBanners(damId) {
    let q = supabase.from('banners_persistentes').select('*').eq('is_active', true);
    if (damId) q = q.or(`dam_id.eq.${damId},dam_id.is.null`);
    const { data, error } = await q;
    if (error) throw error;
    return (data ?? []).map((r) => ({
      id: asBannerId(r.id),
      codigo: r.codigo,
      severidade: r.severidade,
      titulo: r.titulo,
      descricao: r.descricao,
      moduloAlvo: r.modulo_alvo,
      dismissableByProfile: r.dismissable_by_profile,
      isActive: r.is_active,
      damId: r.dam_id ? asDamId(r.dam_id) : null,
      origemRequisito: r.origem_requisito,
    }));
  },

  async listRecomendacoes(damId) {
    let q = supabase.from('recomendacoes').select('*').neq('status', 'concluida').order('created_at', { ascending: false });
    if (damId) q = q.or(`dam_id.eq.${damId},dam_id.is.null`);
    const { data, error } = await q;
    if (error) throw error;
    return (data ?? []).map((r) => ({
      id: asRecomendacaoId(r.id),
      codigo: r.codigo,
      origem: r.origem,
      titulo: r.titulo,
      descricao: r.descricao,
      status: r.status,
      prazo: r.prazo ? new Date(r.prazo) : null,
      responsavelEngineerId: r.responsavel_engineer_id ? asEngineerId(r.responsavel_engineer_id) : null,
      planoAcao: r.plano_acao,
      damId: r.dam_id ? asDamId(r.dam_id) : null,
    }));
  },

  async buscarNoPaebm(_query) {
    return { found: false };
  },

  // GUPM-195 — camadas PAEBM do mapa (M2). Select EXPLÍCITO (nunca `*`): além de
  // evitar o anti-padrão, garante que só campos sem PII saem do banco.
  async listEdificacoesZas() {
    const { data, error } = await supabase
      .from('zas_edificacoes')
      .select('codigo,label,lat,lon,ponto_encontro,residentes,sinore,distancia,nota,ne3')
      .order('codigo')
      .returns<ZasEdificacaoRow[]>();
    if (error) throw error;
    return (data ?? []).map(mapEdificacaoRow);
  },

  async listPontosEncontro() {
    const { data, error } = await supabase
      .from('pontos_encontro')
      .select('codigo,label,tipo,descricao,lat,lon,populacao_estimada,capacidade,tempo_rota,nivel,acesso')
      .order('codigo')
      .returns<PontoEncontroRow[]>();
    if (error) throw error;
    return (data ?? []).map(mapPontoEncontroRow);
  },
};

// TODO[INTEGRACAO-PAEBM-GALVANI]: receber da Galvani:
//   1. PDF do PAEBM Rev.X
//   2. Designação formal do Coordenador PAEBM (PEND-01)
//   3. Cadeia de notificação atualizada (PEND-05)
// → indexar PDF (vector store ou full-text) e implementar buscarNoPaebm.
